/**
 * ServiceFootprint — short narrative + outbound link grid to the full
 * directories on japancardetc.com. Surfaced on the About page directly
 * below the founder's message ("Continuing to think from the ground
 * up...") and above the dark AI-engine section.
 *
 * Owner directive Feb 2026: don't inline 37 installations + every
 * embassy — instead, write a brief summary of who we support and link
 * out to each directory. End with "and many, many more."
 *
 * All cards link to japancardetc.com (the sister-service that owns the
 * directories). External-link icon clarifies the off-site navigation.
 */

import {
  ArrowUpRight,
  Building2,
  Cross,
  Globe2,
  HandHeart,
  MapPin,
  ShieldCheck,
  ShoppingBag,
  Tent,
} from "lucide-react";

const JEC_BASE = "https://japancardetc.com";

// Each card: icon + en/ja title + short en/ja description + outbound URL
// + brand color tokens. Ordered most-known → most-specialized so visitors
// scan the obvious ones first.
const DIRECTORIES = [
  {
    key: "installations",
    href: `${JEC_BASE}/installations`,
    Icon: ShieldCheck,
    color: "#2F8F3C",
    bg: "#ECF6EC",
    ring: "#CDE7CC",
    titleEn: "U.S. Military Installations",
    titleJa: "米軍施設",
    descEn: "37 bases, housing areas, training ranges and support sites across Japan — Navy, Air Force, Marines, Army.",
    descJa: "日本国内37箇所の米軍施設(基地・住宅地区・訓練場・支援拠点) ─ 海軍、空軍、海兵隊、陸軍。",
    countLabelEn: "37 sites",
    countLabelJa: "37箇所",
  },
  {
    key: "embassies",
    href: `${JEC_BASE}/embassies`,
    Icon: Building2,
    color: "#0E5FB0",
    bg: "#EEF6FF",
    ring: "#D6E8FB",
    titleEn: "U.S. Embassy & Consulates",
    titleJa: "米国大使館・領事館",
    descEn: "Tokyo Embassy plus the five U.S. Consulates in Osaka-Kobe, Naha, Fukuoka, Nagoya and Sapporo.",
    descJa: "東京の大使館と、大阪・神戸、那覇、福岡、名古屋、札幌の領事館の計6公館。",
    countLabelEn: "6 missions",
    countLabelJa: "6公館",
  },
  {
    key: "foreign-embassies",
    href: `${JEC_BASE}/foreign-embassies`,
    Icon: Globe2,
    color: "#5B2E83",
    bg: "#F4EEF9",
    ring: "#E9D8F4",
    titleEn: "Foreign Embassies",
    titleJa: "各国大使館",
    descEn: "Diplomatic missions in Japan from every region of the world — long-term partners of the service since the early years.",
    descJa: "世界各国から日本に置かれた在京大使館。創業初期から長期的にご利用いただいています。",
  },
  {
    key: "contractors",
    href: `${JEC_BASE}/directory/contractors`,
    Icon: ShieldCheck,
    color: "#B86E0F",
    bg: "#FFF4E6",
    ring: "#FAD9A6",
    titleEn: "Defense Contractors",
    titleJa: "防衛関連企業",
    descEn: "Cleared contractors, engineering firms and program offices supporting U.S. Forces Japan operations.",
    descJa: "在日米軍の運用を支える防衛関連企業、エンジニアリング会社、プログラムオフィスの皆様。",
  },
  {
    key: "mwr",
    href: `${JEC_BASE}/directory/mwr`,
    Icon: Tent,
    color: "#0E5FB0",
    bg: "#EEF6FF",
    ring: "#D6E8FB",
    titleEn: "MWR Facilities",
    titleJa: "MWR施設",
    descEn: "Morale, Welfare & Recreation centers, recreation lodging and on-base programs across every branch.",
    descJa: "全軍種のモラル・ウェルフェア・レクリエーション(MWR)施設、レクリエーション宿泊、基地内プログラム。",
  },
  {
    key: "exchange",
    href: `${JEC_BASE}/directory/exchange`,
    Icon: ShoppingBag,
    color: "#B86E0F",
    bg: "#FFF4E6",
    ring: "#FAD9A6",
    titleEn: "Exchange (PX / BX / NEX)",
    titleJa: "エクスチェンジ(PX/BX/NEX)",
    descEn: "Army & Air Force Exchange Service (AAFES), Navy Exchange (NEX) and Marine Corps Exchange (MCX) locations.",
    descJa: "陸空軍エクスチェンジ(AAFES)、海軍エクスチェンジ(NEX)、海兵隊エクスチェンジ(MCX)の各拠点。",
  },
  {
    key: "religious",
    href: `${JEC_BASE}/directory/religious`,
    Icon: Cross,
    color: "#5B2E83",
    bg: "#F4EEF9",
    ring: "#E9D8F4",
    titleEn: "Religious & Chapel Programs",
    titleJa: "宗教・チャペルプログラム",
    descEn: "On-base chapels, command chaplaincy programs and faith community groups across the network.",
    descJa: "基地内チャペル、指揮所付チャプレンプログラム、信仰コミュニティの皆様。",
  },
  {
    key: "uso",
    href: `${JEC_BASE}/directory/uso`,
    Icon: HandHeart,
    color: "#2F8F3C",
    bg: "#ECF6EC",
    ring: "#CDE7CC",
    titleEn: "USO Centers",
    titleJa: "USOセンター",
    descEn: "United Service Organizations centers supporting service members and their families in Japan.",
    descJa: "日本国内の米軍関係者とそのご家族を支援するUSO(米国慰問協会)センター。",
  },
];

export default function ServiceFootprint({ lang = "en" }) {
  const isJa = lang === "ja";

  return (
    <section
      id="footprint"
      data-testid="about-service-footprint"
      className="bg-white border-b border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {/* Intro */}
        <div className="max-w-3xl mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold mb-3">
            <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
            {isJa ? "サポートしているコミュニティ" : "The communities we serve"}
          </div>
          <h2
            className={[
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1]",
            ].join(" ")}
          >
            {isJa
              ? "私たちのサービスが届く先 ─ 日本全国へ、しっかりと。"
              : "Where our service actually reaches — quietly, across all of Japan."}
          </h2>
          <p className="mt-5 text-[15px] md:text-[16px] leading-[1.9] text-[#475569]">
            {isJa
              ? "37箇所の米軍施設、6箇所の米国外交公館、各国の在京大使館、防衛関連企業、MWR施設、エクスチェンジ、宗教・チャペルプログラム、USOセンター ─ 日本で生活し、勤務している幅広いコミュニティを、私たちは13年以上にわたり日々お支えしています。それぞれの完全ディレクトリは、運営サイトJapanETCcard.comでご確認いただけます。"
              : "37 U.S. military installations, 6 U.S. diplomatic missions, the foreign embassies across Tokyo, defense contractors, MWR facilities, base exchanges, chaplaincy programs, USO centers — for more than thirteen years we have quietly supported the wide community of people living and working in Japan. Full directories live on our operating site, JapanETCcard.com."}
          </p>
        </div>

        {/* Directory cards */}
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
          data-testid="about-footprint-directories"
        >
          {DIRECTORIES.map(({ key, href, Icon, color, bg, ring, titleEn, titleJa, descEn, descJa, countLabelEn, countLabelJa }) => (
            <li key={key}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`about-footprint-${key}`}
                className="group h-full flex flex-col rounded-2xl p-5 ring-1 transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(15,29,45,0.25)]"
                style={{ backgroundColor: bg, borderColor: ring }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{ backgroundColor: "#fff", color }}
                  >
                    <Icon className="h-4.5 w-4.5" strokeWidth={2} />
                  </span>
                  <ArrowUpRight
                    className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    style={{ color }}
                    strokeWidth={2.25}
                  />
                </div>
                <div
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-[15.5px] md:text-[16px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa ? titleJa : titleEn}
                </div>
                {(countLabelEn || countLabelJa) && (
                  <div
                    className="mt-1.5 text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold"
                    style={{ color }}
                  >
                    {isJa ? countLabelJa : countLabelEn}
                  </div>
                )}
                <p className="mt-2.5 text-[12.5px] leading-[1.7] text-[#475569] flex-1">
                  {isJa ? descJa : descEn}
                </p>
                <div
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold tracking-tight"
                  style={{ color }}
                >
                  {isJa ? "ディレクトリを見る" : "View directory"}
                </div>
              </a>
            </li>
          ))}
        </ul>

        {/* Closing line — owner directive Feb 2026: "...and many, many more." */}
        <p
          data-testid="about-footprint-more"
          className="mt-8 text-[14px] md:text-[15px] leading-[1.85] text-[#64748B] italic"
        >
          {isJa
            ? "そして、他にも多くの地域の皆様にご利用いただいています。"
            : "— and many, many more communities we proudly serve every day."}
        </p>
      </div>
    </section>
  );
}
